// Live audio visualizer for the Now Playing hero.
//
// Strategy:
// 1. Try Web Audio API: createMediaElementSource() → AnalyserNode → 48 bars rendered via canvas.
//    Each <audio>/<video> element can only be source-wrapped ONCE per AudioContext, so we cache
//    the wrapper on the element via a WeakMap.
// 2. Fall back to a CSS-only "fake equalizer" animation if Web Audio fails (autoplay policy,
//    CORS-tainted media element, browser w/ no AudioContext support).
//
// Visual: 32 vertical gold/amber bars across the bottom of the cover artwork. When paused or
// stopped, bars decay to 0 within ~600ms.

import React, { useEffect, useRef, useState } from "react";
import { getMediaWrapper, resumeAudioContextOnGesture } from "../lib/webAudio";

const BAR_COUNT = 32;

export default function Visualizer({ mediaEl, isPlaying, height = 80 }) {
  const canvasRef = useRef(null);
  const fakePhaseRef = useRef(0);
  const lastBarsRef = useRef(new Float32Array(BAR_COUNT));
  const rafRef = useRef(null);
  const [analyserAvailable, setAnalyserAvailable] = useState(false);
  const [analyserChecked, setAnalyserChecked] = useState(false);

  // Re-check analyser availability whenever the underlying media element changes.
  useEffect(() => {
    setAnalyserChecked(false);
    if (!mediaEl) {
      setAnalyserAvailable(false);
      setAnalyserChecked(true);
      return;
    }
    const wrap = getMediaWrapper(mediaEl);
    setAnalyserAvailable(!!wrap);
    setAnalyserChecked(true);
  }, [mediaEl]);

  // Resume context on play (autoplay-policy compliance — needs a user gesture)
  useEffect(() => {
    if (!isPlaying) return;
    resumeAudioContextOnGesture();
  }, [isPlaying]);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx2d = canvas.getContext("2d");
    if (!ctx2d) return;

    const wrap = mediaEl ? getMediaWrapper(mediaEl) : null;
    const analyser = wrap?.analyser || null;
    const buf = analyser ? new Uint8Array(analyser.frequencyBinCount) : null;

    function dpr() { return Math.min(window.devicePixelRatio || 1, 2); }
    function resize() {
      const r = canvas.getBoundingClientRect();
      const ratio = dpr();
      canvas.width = Math.floor(r.width * ratio);
      canvas.height = Math.floor(r.height * ratio);
    }
    resize();
    window.addEventListener("resize", resize);

    function draw() {
      const w = canvas.width, h = canvas.height;
      ctx2d.clearRect(0, 0, w, h);
      let bars;
      if (analyser && buf && isPlaying) {
        analyser.getByteFrequencyData(buf);
        bars = new Float32Array(BAR_COUNT);
        // Down-sample frequency bins to BAR_COUNT bars (skewed log-ish).
        for (let i = 0; i < BAR_COUNT; i++) {
          const start = Math.floor(Math.pow(i / BAR_COUNT, 1.6) * buf.length);
          const end = Math.max(start + 1, Math.floor(Math.pow((i + 1) / BAR_COUNT, 1.6) * buf.length));
          let sum = 0;
          for (let j = start; j < end && j < buf.length; j++) sum += buf[j];
          bars[i] = sum / Math.max(1, end - start) / 255;
        }
      } else if (isPlaying) {
        // Fallback "fake EQ" when Web Audio analyser isn't usable.
        fakePhaseRef.current += 0.085;
        bars = new Float32Array(BAR_COUNT);
        for (let i = 0; i < BAR_COUNT; i++) {
          const t = fakePhaseRef.current + i * 0.32;
          const n = 0.55 + 0.32 * Math.sin(t) + 0.16 * Math.sin(t * 2.7) + 0.10 * Math.sin(t * 5.3);
          bars[i] = Math.max(0, Math.min(1, n));
        }
      } else {
        // Decay toward 0 when paused / stopped.
        bars = new Float32Array(BAR_COUNT);
        for (let i = 0; i < BAR_COUNT; i++) bars[i] = lastBarsRef.current[i] * 0.85;
      }
      // Smooth between previous frame and current (extra polish on top of analyser smoothing)
      const last = lastBarsRef.current;
      for (let i = 0; i < BAR_COUNT; i++) bars[i] = last[i] * 0.55 + bars[i] * 0.45;
      lastBarsRef.current = bars;

      const gap = Math.max(2 * dpr(), 3);
      const barW = (w - gap * (BAR_COUNT + 1)) / BAR_COUNT;
      for (let i = 0; i < BAR_COUNT; i++) {
        const v = bars[i];
        const barH = Math.max(2 * dpr(), v * h);
        const x = gap + i * (barW + gap);
        const y = h - barH;
        // Gold gradient
        const grad = ctx2d.createLinearGradient(0, y, 0, h);
        grad.addColorStop(0, "rgba(220,194,134,0.95)");
        grad.addColorStop(0.55, "rgba(197,160,89,0.85)");
        grad.addColorStop(1, "rgba(110,85,40,0.65)");
        ctx2d.fillStyle = grad;
        ctx2d.fillRect(x, y, Math.max(barW, 1), barH);
        // Highlight cap
        if (v > 0.04) {
          ctx2d.fillStyle = "rgba(253,251,247,0.85)";
          ctx2d.fillRect(x, y, Math.max(barW, 1), Math.min(2 * dpr(), barH));
        }
      }
      rafRef.current = requestAnimationFrame(draw);
    }
    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [mediaEl, isPlaying, analyserChecked]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="visualizer-canvas"
      data-mode={analyserAvailable ? "analyser" : "fallback"}
      style={{ width: "100%", height: `${height}px`, display: "block", borderRadius: 8 }}
      aria-hidden="true"
    />
  );
}
